import { yupResolver } from '@hookform/resolvers/yup';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { MobileDatePicker } from '@mui/x-date-pickers';
import { addDays, differenceInDays } from 'date-fns';
import numeral from 'numeral';
import { Controller, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { translateKey } from 'src/locales/translate';


interface CampaignStep2BudgetProps {
  initialValues: {
    dailyBudget: number;
    startDate: Date | null;
    endDate: Date | null;
  };
  onNext: (values: {
    dailyBudget: number;
    startDate: Date;
    endDate: Date;
  }) => void;
  onBack: () => void;
  isLoading: boolean;
}

const schema = Yup.object().shape({
  dailyBudget: Yup.number()
    .required(translateKey("hardcoded.budzet_dzienny_jest_wymagany"))
    .min(10, translateKey("hardcoded.minimalny_budzet_dzienny_to_10_zl"))
    .max(10000, translateKey("hardcoded.maksymalny_budzet_dzienny_to_10_000_zl")),
  startDate: Yup.date()
    .required(translateKey("hardcoded.data_rozpoczecia_jest_wymagana"))
    .min(new Date(), translateKey("hardcoded.data_rozpoczecia_musi_byc_w_przyszlosci")),
  endDate: Yup.date()
    .required(translateKey("hardcoded.data_zakonczenia_jest_wymagana"))
    .min(
      Yup.ref('startDate'),
      translateKey("hardcoded.data_zakonczenia_musi_byc_po_dacie_rozpoczecia"),
    ),
});

export default function CampaignStep2Budget({
  initialValues,
  onNext,
  onBack,
  isLoading,
}: CampaignStep2BudgetProps) {
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      dailyBudget: initialValues.dailyBudget,
      startDate: initialValues.startDate || addDays(new Date(), 1),
      endDate: initialValues.endDate || addDays(new Date(), 31),
    },
  });

  const dailyBudget = watch('dailyBudget');
  const startDate = watch('startDate');
  const endDate = watch('endDate');

  const campaignDays =
    startDate && endDate
      ? Math.max(1, differenceInDays(endDate, startDate) + 1)
      : 0;
  const totalBudget = dailyBudget * campaignDays;

  const onSubmit = (data: {
    dailyBudget: number;
    startDate: Date;
    endDate: Date;
  }) => {
    onNext(data);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        <Controller
          name="dailyBudget"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label={translateKey("hardcoded.budzet_dzienny")}
              fullWidth
              error={!!errors.dailyBudget}
              helperText={errors.dailyBudget?.message}
              inputProps={{ inputMode: 'numeric' }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">{translateKey("quickBooking.currencySuffix")}</InputAdornment>
                ),
              }}
              onKeyDown={(e) => {
                if (e.key === '-' || e.key === '+' || e.key === 'e') {
                  e.preventDefault();
                }
              }}
              onChange={(e) => {
                const raw = e.target.value.replace(/^0+(?=\d)/, '');
                const parsed = parseFloat(raw);
                field.onChange(Number.isNaN(parsed) ? '' : parsed);
              }}
            />
          )}
        />

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Controller
            name="startDate"
            control={control}
            render={({ field }) => (
              <MobileDatePicker
                {...field}
                label={translateKey("hardcoded.data_rozpoczecia")}
                minDate={addDays(new Date(), 1)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    error={!!errors.startDate}
                    helperText={errors.startDate?.message as string}
                  />
                )}
              />
            )}
          />

          <Controller
            name="endDate"
            control={control}
            render={({ field }) => (
              <MobileDatePicker
                {...field}
                label={translateKey("hardcoded.data_zakonczenia")}
                minDate={
                  startDate ? addDays(startDate, 1) : addDays(new Date(), 2)
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    error={!!errors.endDate}
                    helperText={errors.endDate?.message as string}
                  />
                )}
              />
            )}
          />
        </Box>

        <Alert severity="info">
          <Typography variant="subtitle2" sx={{ mb: 1 }}>{translateKey("hardcoded.podsumowanie_budzetu")}</Typography>
          <Stack spacing={0.5}>
            <Typography variant="body2">{translateKey("hardcoded.budzet_dzienny_2")}{' '}
              <strong>{numeral(dailyBudget).format('0,0')}{translateKey("quickBooking.currencySuffix")}</strong>
            </Typography>
            <Typography variant="body2">
              Czas trwania: <strong>{campaignDays} dni</strong>
            </Typography>
            <Typography variant="body2">{translateKey("hardcoded.calkowity_budzet")}{' '}
              <strong>{numeral(totalBudget).format('0,0')}{translateKey("quickBooking.currencySuffix")}</strong>
            </Typography>
          </Stack>
        </Alert>

        <Stack direction="row" justifyContent="space-between" sx={{ mt: 3 }}>
          <Button variant="outlined" color="inherit" onClick={onBack}>{translateKey("quickBooking.back")}</Button>
          <Button type="submit" variant="contained" disabled={isLoading}>
            {isLoading ? <CircularProgress size={24} /> : translateKey("quickBooking.next")}
          </Button>
        </Stack>
      </Stack>
    </form>
  );
}
